import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UIRootState } from '../../../store/reducers';
import { closeSettingsModal } from '../../../store/actions/gameUIActions';
import './SettingsModal.css';

export const SettingsModal: React.FC = () => {
  const dispatch = useDispatch();
  const settings = useSelector((state: UIRootState) => state.gameUI.settings);
  const labels = useSelector((state: UIRootState) => state.gameUI.labels);
  const isOpen = useSelector((state: UIRootState) => state.gameUI.modals.settingsModal.isOpen);

  const [panelVisible, setPanelVisible] = React.useState<boolean>(false);
  const [panelClosing, setPanelClosing] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (isOpen) {
      setPanelVisible(true);
      setPanelClosing(true);
      const t = setTimeout(() => setPanelClosing(false), 0);
      return () => clearTimeout(t);
    } else if (panelVisible) {
      setPanelClosing(true);
      const t = setTimeout(() => {
        setPanelVisible(false);
        setPanelClosing(false);
      }, 360);
      return () => clearTimeout(t);
    }
  }, [isOpen]);


  const handleButtonClick = (buttonKey: string, data?: any) => {
    document.dispatchEvent(new CustomEvent('ui-button-click', {
      detail: { buttonKey, data }
    }));
  };

  const handleSettingChange = (settingKey: string) => {
    document.dispatchEvent(new CustomEvent('ui-setting-change', {
      detail: { settingKey }
    }));
  };

  useEffect(() => {
    const handleUIEvent = (event: CustomEvent) => {
      const detail = event.detail;
      if (detail.buttonKey === 'close-settings') {
        dispatch(closeSettingsModal());
      }
      // Feedback button is handled directly by GameUI, no need to handle it here
    };

    document.addEventListener('ui-button-click', handleUIEvent as EventListener);
    return () => document.removeEventListener('ui-button-click', handleUIEvent as EventListener);
  }, [dispatch]);

  // GameUI handles 'ui-setting-change' events directly, no need to duplicate here

  if (!panelVisible) return null;

  return (
    <div 
      id="game-ui-options" 
      className={`settings-modal text-white flex-column h-100 w-100 panel--open ${panelClosing ? 'panel--closing' : ''}`}
    >
      <div className="settings-content-wrapper">
        <div className="settings-header d-flex justify-content-between align-items-center mb-4">
          <h5 id="settings-header" className="settings-title mb-0 text-white">{labels.settingsText}</h5>
          <button 
            id="closeOptionsButton" 
            className="btn btn-link text-white p-0 border-0 settings-close" 
            onClick={() => handleButtonClick('close-settings')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor"/>
            </svg>
          </button>
        </div>

      {/* Dynamic Settings Options */}
      <div id="dynamic-checkboxes" className="d-flex flex-column checkboxes settings-checkboxes">
        {settings.map((setting) => (
          setting.visible && (
            <div 
              key={setting.key}
              className="checkbox-item"
              data-setting-key={setting.key}
            >
              <div 
                className="checkbox-content"
              >
                <span className="checkbox-label">
                  {setting.label}
                </span>
                <div className="form-check form-switch checkbox-switch">
                  <input
                    id={`dynamicCheckbox_${setting.key}`}
                    type="checkbox"
                    checked={setting.checked}
                    onChange={() => handleSettingChange(setting.key)}
                    className="form-check-input"
                  />
                </div>
              </div>
              <div 
                className="divider-secondary"
              />
            </div>
          )
        ))}
      </div>

      {/* Feedback Button */}
        <div 
          id="feedbackButton" 
          className="d-flex align-items-center justify-content-center settings-feedback"
          onClick={() => handleButtonClick('feedback')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path fillRule="evenodd" clipRule="evenodd" d="M3 3H21C22.1 3 23 3.9 23 5V17C23 18.1 22.1 19 21 19H13.9L10.2 21.7C10 21.9 9.7 22 9.5 22H9C8.4 22 8 21.6 8 21V19H3C1.9 19 1 18.1 1 17V5C1 3.9 1.9 3 3 3ZM6 7C5.44772 7 5 7.44772 5 8C5 8.55228 5.44772 9 6 9H18C18.5523 9 19 8.55228 19 8C19 7.44772 18.5523 7 18 7H6ZM6 12C5.44772 12 5 12.4477 5 13C5 13.5523 5.44772 14 6 14H18C18.5523 14 19 13.5523 19 13C19 12.4477 18.5523 12 18 12H6Z" fill="white"/>
          </svg>
                    <span id="feedback-button-text">{labels.feedbackText}</span>
        </div>
      </div>
    </div>
  );
}; 